import React, { useState } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage } from '@/utils/layer'
import { Column } from '@/components/layout/layout'

import './stock-search-detail.scss'
import { getBEMCls } from '@/utils/classname'
import { DatePicker, Table, Spin } from 'antd'
import FormTitle from '../../../components/form/form-title/form-title'
import { useRequest, usePersistFn } from 'ahooks'
import { $post } from '@/api'
import { StockList } from '../stock-search-table/stock-search-table'
import { IPagination } from '@/model/types/common'
import { Goods } from '@/model/types/goods'
import $msg from '@/utils/message'
import { WithScrollLoad } from '@/utils/utils-widget'
import { ColumnsType } from 'antd/lib/table'
import { isArray, formatTime } from '@/utils'
import { stockChangeType } from '../../../goods-storage/goods-library/modules/goods-detail/modules/stock_change_record'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { getQiniuImgUrl } from '@/config/qiniu'
import dayjs, { Dayjs, OpUnitType } from 'dayjs'

interface StockSearchDetailProps {
    stockId: number
}

interface StockRecordList {
    change_time: string
    goods: Goods
    order_no: string
    reason: number
    stock_after_change: number
    stock_changed: number
}

const StockSearchDetail: React.FC<StockSearchDetailProps> = props => {
    const cls = (...arg: string[]) => getBEMCls('stock-search-detail', ...arg)
    const [loading, setLoading] = useState<boolean>(true)

    const formatTimes = (time: Dayjs) => time.format('YYYY-MM-DD')

    const [date, setDate] = useState<string[]>(() => [formatTimes(dayjs().subtract(1, 'w')), formatTimes(dayjs())])
    const [slectTab, setSelectTab] = useState<number>(0)

    const [stockInfo, setStockInfo] = useState<StockList | null>(null)

    const setAllData = useSetDataTogether()

    useRequest(
        () =>
            $post({
                url: '/warehouse/getgoodsstockinfos',
                params: {
                    goods_id: props.stockId,
                    from_date: date[0],
                    to_date: date[1],
                },
            }),
        {
            onSuccess: res => {
                setAllData(() => {
                    setStockInfo(res.stock_infos[0])
                    setLoading(false)
                })
            },
            refreshDeps: [date[0]],
        },
    )

    const requestHandle = useRequest<
        {
            list: StockRecordList[]
            pagination: IPagination
        },
        {
            records: StockRecordList[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/goods/getgoodsstockchangerecords',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    goods_id: props.stockId,
                    from_date: date[0],
                    to_date: date[1],
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.records,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
            refreshDeps: [date[0]],
        },
    )

    const columns: ColumnsType<StockRecordList> = [
        {
            title: '#',
            align: 'center',
            width: '40px',
            key: '#',
            render: (text, record, index) => <div>{index + 1}</div>,
        },
        {
            title: '时间',
            render: (text, record) => (
                <div data-column>
                    {!isArray(formatTime(record.change_time)) ? (
                        // 需要根据实际数据处理
                        <span>{formatTime(record.change_time)}</span>
                    ) : (
                        <>
                            <span>{formatTime(record.change_time)[0]}</span>
                            <span>{formatTime(record.change_time)[1]}</span>
                        </>
                    )}
                </div>
            ),
        },
        {
            title: '变更类型',
            key: 'reason',
            dataIndex: 'reason',
            render: text => <div>{stockChangeType(text)}</div>,
        },
        {
            title: '变更库存',
            key: '',
            align: 'right',
            dataIndex: 'stock_changed',
            render: (value, record) => (
                <span className={[value < 0 ? 'danger-color' : ''].join(' ')}>
                    {value > 0 ? '+' : ''}
                    {value}
                    {record.goods.stock_unit.name}
                </span>
            ),
        },
        {
            title: '剩余库存',
            align: 'right',
            key: 'stock_after_change',
            dataIndex: 'stock_after_change',
            render: (value, record) => (
                <span className={[value < 0 ? 'danger-color' : ''].join(' ')}>
                    {value > 0 ? '+' : ''}
                    {value}
                    {record.goods.stock_unit.name}
                </span>
            ),
        },
    ]

    const handelSelectTime = usePersistFn((m: OpUnitType, id: number, num: number = 1) => {
        setAllData(() => {
            setDate([formatTimes(dayjs().subtract(num, m)), formatTimes(dayjs())])
            setSelectTab(id)
        })
    })

    const selectTimeList = [
        {
            id: 0,
            text: '本周',
            onClick: () => {
                handelSelectTime('w', 0)
            },
        },
        {
            id: 1,
            text: '本月',
            onClick: () => {
                handelSelectTime('month', 1)
            },
        },
        {
            id: 2,
            text: '近半年',
            onClick: () => {
                handelSelectTime('month', 2, 6)
            },
        },
        {
            id: 3,
            text: '近一年',
            onClick: () => {
                handelSelectTime('y', 3)
            },
        },
    ]

    return (
        <BaseDrawer
            isShow
            title="商品库存详情"
            width={720}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column
                data-align-items-center
                data-height-100p
                data-overflow-auto
                data-padding-bottom-20
                data-position-relative
            >
                {loading && (
                    <div className={cls('loading')}>
                        <Spin spinning={loading} />
                    </div>
                )}
                <img src={getQiniuImgUrl(stockInfo?.img_url)} className={cls('avatar')} alt="1" />
                <div className={cls('title')}>{stockInfo?.goods_name || '1'}</div>
                <div className={cls('tab')}>
                    {selectTimeList.map(i => (
                        <div
                            key={i.id}
                            onClick={i.onClick}
                            className={cls('tab', slectTab === i.id ? 'item-active' : 'item')}
                        >
                            {i.text}
                        </div>
                    ))}
                    <div className="time-picker">
                        <DatePicker.RangePicker
                            dateRender={current => (
                                <div
                                    className="ant-picker-cell-inner"
                                    style={{
                                        borderRadius: '50%',
                                    }}
                                >
                                    {current.date()}
                                </div>
                            )}
                            onChange={(monent, dateString) => {
                                if (dateString[0]) {
                                    setAllData(() => {
                                        setDate(dateString)
                                        setSelectTab(4)
                                    })
                                } else {
                                    handelSelectTime('w', 0)
                                }
                            }}
                            suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        />
                    </div>
                </div>
                <div className={cls('data')}>
                    <div className={cls('data', 'item')}>
                        <div className="txt">
                            <div className="num">{stockInfo?.opening_stock}</div>
                            <div className="unit">{stockInfo?.stock_unit.name}</div>
                        </div>
                        <div className="des">期初库存</div>
                    </div>
                    <div className={cls('data', 'item')}>
                        <div className="txt">
                            <div className="num">{stockInfo?.closing_stock}</div>
                            <div className="unit">{stockInfo?.stock_unit.name}</div>
                        </div>
                        <div className="des">期末库存</div>
                    </div>
                    <div className={cls('data', 'item')}>
                        <div className="txt">
                            <div className="num">{stockInfo?.current_stock}</div>
                            <div className="unit">{stockInfo?.stock_unit.name}</div>
                        </div>
                        <div className="des">实时库存</div>
                    </div>
                </div>
                <div className={cls('ft')}>
                    <FormTitle title="库存变更纪录" hasLine={false} />
                </div>
                <div className={cls('table-container')}>
                    <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                        <Table
                            rowKey={record => record.goods.id}
                            loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                            bordered
                            pagination={false}
                            columns={columns}
                            dataSource={requestHandle.data?.list}
                            scroll={{ x: 'max-content', y: 398 }}
                        />
                    </WithScrollLoad>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default StockSearchDetail
